import { toast } from 'react-toastify';

/**
 * Generate unique node ID
 * @param {string} nodeType - Type of the node
 * @returns {string} Unique node ID
 */
export const generateNodeId = (nodeType) => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 11);
  return `${nodeType}_${timestamp}_${random}`;
};

/**
 * Generate default configuration from config fields
 * @param {Array} configFields - Configuration field definitions
 * @returns {Object} Default configuration object
 */
export const generateDefaultConfig = (configFields) => {
  const config = {};
  
  configFields.forEach(field => {
    if (field.defaultValue !== undefined) {
      config[field.name] = field.defaultValue;
    } else {
      // Set appropriate default based on field type
      switch (field.type) {
        case 'boolean':
          config[field.name] = false;
          break;
        case 'number':
          config[field.name] = 0;
          break;
        case 'select':
          config[field.name] = field.options?.[0] || '';
          break;
        default:
          config[field.name] = '';
      }
    }
  });
  
  return config;
};

/**
 * Create a new node instance with default configuration
 * @param {Object} nodeTemplate - The node template from database
 * @param {Object} position - Position coordinates {x, y}
 * @param {Object} customConfig - Custom configuration overrides
 * @returns {Object} New node instance for ReactFlow
 */
export const createNodeInstance = (nodeTemplate, position = { x: 0, y: 0 }, customConfig = {}) => {
  const nodeId = generateNodeId(nodeTemplate.nodeDefinition?.nodeType || 'node');
  const defaultConfig = generateDefaultConfig(nodeTemplate.nodeDefinition?.configFields || []);
  
  return {
    id: nodeId,
    type: nodeTemplate.nodeDefinition?.nodeType || 'node', // Use actual node type
    position,
    data: {
      ...nodeTemplate,
      label: nodeTemplate.name,
      nodeDefinition: nodeTemplate.nodeDefinition,
      iconSvg: nodeTemplate.iconSvg,
      config: {
        ...defaultConfig,
        ...customConfig
      }
    }
  };
};

/**
 * WorkflowNodeService - Consolidated service for all node-related operations
 * Handles node behaviors, integrations, and field validations dynamically
 */

/**
 * NodeBehaviorService - Manages dynamic node behaviors based on configuration
 */
export class NodeBehaviorService {
  constructor(nodeTemplates = {}) {
    this.nodeTemplates = nodeTemplates;
    this.behaviorCache = new Map();
  }

  /**
   * Get behavior configuration for a node type
   */
  getBehavior(nodeType) {
    if (this.behaviorCache.has(nodeType)) {
      return this.behaviorCache.get(nodeType);
    }

    const nodeTemplate = this.nodeTemplates[nodeType];
    const behaviors = nodeTemplate?.nodeDefinition?.behaviors || {};
    
    this.behaviorCache.set(nodeType, behaviors);
    return behaviors;
  }

  /**
   * Check if node requires integration
   */
  requiresIntegration(nodeType) {
    const behaviors = this.getBehavior(nodeType);
    return behaviors.requiresIntegration?.enabled || false;
  }

  /**
   * Get integration requirements for a node
   */
  getIntegrationRequirements(nodeType, config) {
    const behaviors = this.getBehavior(nodeType);
    const integrationConfig = behaviors.requiresIntegration;
    
    if (!integrationConfig?.enabled) {
      return null;
    }

    const fromFieldValue = config[integrationConfig.fromField];
    const mapping = integrationConfig.mapping || {};
    
    return mapping[fromFieldValue] || null;
  }

  /**
   * Check if node requires call resources
   */
  requiresCallResources(nodeType) {
    const behaviors = this.getBehavior(nodeType);
    return behaviors.requiresCallResources || false;
  }

  /**
   * Get fields to preserve when loading node config
   */
  getPreserveFields(nodeType) {
    const behaviors = this.getBehavior(nodeType);
    return behaviors.preserveConfigFields || [];
  }

  /**
   * Apply field mappings to config
   */
  applyFieldMappings(nodeType, config, direction = 'toUI') {
    const behaviors = this.getBehavior(nodeType);
    const mappings = behaviors.fieldMappings || {};
    
    if (Object.keys(mappings).length === 0) {
      return config;
    }

    const mappedConfig = { ...config };
    
    Object.entries(mappings).forEach(([sourceField, targetField]) => {
      if (direction === 'toUI') {
        if (config[sourceField] !== undefined) {
          mappedConfig[targetField] = config[sourceField];
          delete mappedConfig[sourceField];
        }
      } else {
        if (config[targetField] !== undefined) {
          mappedConfig[sourceField] = config[targetField];
          delete mappedConfig[targetField];
        }
      }
    });
    
    return mappedConfig;
  }

  /**
   * Check if node has multiple inputs
   */
  hasMultipleInputs(nodeType) {
    const behaviors = this.getBehavior(nodeType);
    return behaviors.hasMultipleInputs || false;
  }

  /**
   * Get success message template for node
   */
  getSuccessMessage(nodeType, config) {
    const behaviors = this.getBehavior(nodeType);
    const template = behaviors.successMessage || 'Node executed successfully';
    
    // Replace placeholders in template
    return template.replace(/{{(\w+)}}/g, (match, key) => {
      return config[key] || match;
    });
  }
}

/**
 * IntegrationMappingService - Handles integration service mappings
 */
export class IntegrationMappingService {
  constructor(nodeTemplates = {}) {
    this.nodeTemplates = nodeTemplates;
    this.integrationCache = new Map();
  }

  /**
   * Get integration config for a node type
   */
  getIntegrationConfig(nodeType) {
    if (this.integrationCache.has(nodeType)) {
      return this.integrationCache.get(nodeType);
    }

    const nodeTemplate = this.nodeTemplates[nodeType];
    const integrationConfig = nodeTemplate?.nodeDefinition?.integrationConfig || {};
    
    this.integrationCache.set(nodeType, integrationConfig);
    return integrationConfig;
  }

  /**
   * Check if node listens for specific events
   */
  listensForEvent(nodeType, eventType) {
    const config = this.getIntegrationConfig(nodeType);
    const events = config.events || [];
    return events.includes(eventType);
  }

  /**
   * Get required services for a node
   */
  getRequiredServices(nodeType) {
    const config = this.getIntegrationConfig(nodeType);
    return config.requiredServices || [];
  }

  /**
   * Get connection component mapping
   */
  getConnectionComponent(nodeType, channel) {
    const config = this.getIntegrationConfig(nodeType);
    const connectionComponents = config.connectionComponents || {};
    return connectionComponents[channel] || null;
  }

  /**
   * Check if integration is connected
   */
  async checkIntegrationStatus(service, statusAction) {
    try {
      // This would typically call your backend API
      // For now, return a mock response
      console.log(`Checking integration status: ${service} - ${statusAction}`);
      return { connected: false, message: 'Integration check not implemented' };
    } catch (error) {
      console.error('Error checking integration status:', error);
      return { connected: false, error: error.message };
    }
  }
}

/**
 * FieldValidationService - Handles field validation rules
 */
export class FieldValidationService {
  constructor(nodeTemplates = {}) {
    this.nodeTemplates = nodeTemplates;
    this.validationCache = new Map();
  }

  /**
   * Get validation rules for a field
   */
  getFieldValidation(nodeType, fieldName) {
    const cacheKey = `${nodeType}.${fieldName}`;
    
    if (this.validationCache.has(cacheKey)) {
      return this.validationCache.get(cacheKey);
    }

    const nodeTemplate = this.nodeTemplates[nodeType];
    const validationRules = nodeTemplate?.nodeDefinition?.fieldConfigurations?.validationRules || {};
    const fieldValidation = validationRules[fieldName] || {};
    
    this.validationCache.set(cacheKey, fieldValidation);
    return fieldValidation;
  }

  /**
   * Validate field value
   */
  validateField(nodeType, fieldName, value, allValues = {}) {
    const validation = this.getFieldValidation(nodeType, fieldName);
    const errors = [];

    // Required validation
    if (validation.required && !value) {
      errors.push(validation.requiredMessage || `${fieldName} is required`);
    }

    // Pattern validation
    if (validation.pattern && value) {
      const regex = new RegExp(validation.pattern);
      if (!regex.test(value)) {
        errors.push(validation.patternMessage || `${fieldName} format is invalid`);
      }
    }

    // Custom validation function
    if (validation.custom && typeof validation.custom === 'function') {
      const customError = validation.custom(value, allValues);
      if (customError) {
        errors.push(customError);
      }
    }

    // Min/max length for strings
    if (typeof value === 'string') {
      if (validation.minLength && value.length < validation.minLength) {
        errors.push(`${fieldName} must be at least ${validation.minLength} characters`);
      }
      if (validation.maxLength && value.length > validation.maxLength) {
        errors.push(`${fieldName} must be no more than ${validation.maxLength} characters`);
      }
    }

    // Min/max for numbers
    if (typeof value === 'number') {
      if (validation.min !== undefined && value < validation.min) {
        errors.push(`${fieldName} must be at least ${validation.min}`);
      }
      if (validation.max !== undefined && value > validation.max) {
        errors.push(`${fieldName} must be no more than ${validation.max}`);
      }
    }

    return errors;
  }

  /**
   * Validate entire node configuration
   */
  validateNodeConfig(nodeType, config) {
    const nodeTemplate = this.nodeTemplates[nodeType];
    const configFields = nodeTemplate?.nodeDefinition?.configFields || [];
    const errors = {};

    configFields.forEach(field => {
      const fieldErrors = this.validateField(nodeType, field.name, config[field.name], config);
      if (fieldErrors.length > 0) {
        errors[field.name] = fieldErrors;
      }
    });

    return errors;
  }
}

/**
 * NodeDefinitionService - Service for accessing and managing node definitions
 * This service provides a centralized way to access node configurations,
 * behaviors, and settings from the database-driven node definitions.
 */
export class NodeDefinitionService {
  constructor(nodeTools = []) {
    this.nodeTools = nodeTools;
    this._definitionsCache = new Map();
    this._buildCache();
  }

  /**
   * Build cache of node definitions for quick access
   */
  _buildCache() {
    // Handle nodeTools being either an array or an object with data property
    const toolsArray = Array.isArray(this.nodeTools) ? this.nodeTools : (this.nodeTools?.data || []);
    
    if (!Array.isArray(toolsArray)) {
      console.warn('NodeDefinitionService: nodeTools is not an array', this.nodeTools);
      return;
    }
    
    toolsArray.forEach(tool => {
      if (tool.nodeDefinition?.nodeType) {
        this._definitionsCache.set(tool.nodeDefinition.nodeType, tool.nodeDefinition);
      }
    });
  }

  /**
   * Get node definition by node type
   * @param {string} nodeType - The type of the node
   * @returns {Object|null} The node definition or null
   */
  getNodeDefinition(nodeType) {
    return this._definitionsCache.get(nodeType) || null;
  }

  /**
   * Get default configuration for a node type
   * @param {string} nodeType - The type of the node
   * @returns {Object} Default configuration object
   */
  getDefaultConfig(nodeType) {
    const definition = this.getNodeDefinition(nodeType);
    if (!definition) return {};

    const defaults = {};
    definition.configFields?.forEach(field => {
      if (field.defaultValue !== undefined) {
        defaults[field.name] = field.defaultValue;
      }
    });
    return defaults;
  }

  /**
   * Get specific behavior configuration for a node
   * @param {string} nodeType - The type of the node
   * @param {string} behaviorName - Name of the behavior
   * @returns {*} The behavior value or undefined
   */
  getNodeBehavior(nodeType, behaviorName) {
    const definition = this.getNodeDefinition(nodeType);
    return definition?.behaviors?.[behaviorName];
  }

  /**
   * Check if node requires integration
   * @param {string} nodeType - The type of the node
   * @returns {boolean} True if node requires integration
   */
  nodeRequiresIntegration(nodeType) {
    return this.getNodeBehavior(nodeType, 'requiresIntegration')?.enabled || false;
  }

  /**
   * Get integration mapping for a node
   * @param {string} nodeType - The type of the node
   * @param {string} fieldValue - Value of the field that determines integration
   * @returns {Object|null} Integration mapping configuration
   */
  getIntegrationMapping(nodeType, fieldValue) {
    const requiresIntegration = this.getNodeBehavior(nodeType, 'requiresIntegration');
    if (!requiresIntegration?.enabled) return null;

    const fromField = requiresIntegration.fromField;
    const mapping = requiresIntegration.mapping;
    
    return mapping?.[fieldValue] || null;
  }

  /**
   * Get UI message for a node
   * @param {string} nodeType - The type of the node
   * @param {string} messageType - Type of message (success, error, infoAlerts, toasts)
   * @param {string} subType - Sub-type of message
   * @returns {string|null} The message or null
   */
  getUIMessage(nodeType, messageType, subType) {
    const definition = this.getNodeDefinition(nodeType);
    return definition?.uiMessages?.[messageType]?.[subType] || null;
  }

  /**
   * Get field configuration
   * @param {string} nodeType - The type of the node
   * @param {string} configName - Name of the configuration
   * @returns {*} Field configuration value
   */
  getFieldConfiguration(nodeType, configName) {
    const definition = this.getNodeDefinition(nodeType);
    return definition?.fieldConfigurations?.[configName];
  }

  /**
   * Get test configuration for a node
   * @param {string} nodeType - The type of the node
   * @param {string} testType - Type of test (e.g., 'webhook')
   * @returns {Object|null} Test configuration
   */
  getTestConfig(nodeType, testType) {
    const definition = this.getNodeDefinition(nodeType);
    return definition?.testConfig?.[testType] || null;
  }

  /**
   * Get preserved fields for a node type
   * @param {string} nodeType - The type of the node
   * @returns {Array} Array of field names to preserve
   */
  getPreservedFields(nodeType) {
    return this.getNodeBehavior(nodeType, 'preserveConfigFields') || [];
  }

  /**
   * Get field mappings for a node type
   * @param {string} nodeType - The type of the node
   * @returns {Object} Field mappings configuration
   */
  getFieldMappings(nodeType) {
    return this.getNodeBehavior(nodeType, 'fieldMappings') || {};
  }

  /**
   * Check if node has multiple inputs
   * @param {string} nodeType - The type of the node
   * @returns {boolean} True if node supports multiple inputs
   */
  hasMultipleInputs(nodeType) {
    return this.getNodeBehavior(nodeType, 'hasMultipleInputs') || false;
  }

  /**
   * Check if node has multiple outputs
   * @param {string} nodeType - The type of the node
   * @returns {boolean} True if node supports multiple outputs
   */
  hasMultipleOutputs(nodeType) {
    return this.getNodeBehavior(nodeType, 'hasMultipleOutputs') || false;
  }

  /**
   * Get integration configuration
   * @param {string} nodeType - The type of the node
   * @returns {Object} Integration configuration
   */
  getIntegrationConfig(nodeType) {
    const definition = this.getNodeDefinition(nodeType);
    return definition?.integrationConfig || {};
  }

  /**
   * Get dropdown options for quick configuration
   * @param {string} nodeType - The type of the node
   * @returns {Array} Array of quick configuration options
   */
  getQuickConfigOptions(nodeType) {
    const definition = this.getNodeDefinition(nodeType);
    return definition?.options || [];
  }

  /**
   * Process template string with values
   * @param {string} template - Template string with {{variable}} placeholders
   * @param {Object} values - Values to replace in template
   * @returns {string} Processed string
   */
  processTemplate(template, values) {
    if (!template || typeof template !== 'string') return template;
    
    return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return values[key] !== undefined ? values[key] : match;
    });
  }

  /**
   * Update node tools and rebuild cache
   * @param {Array|Object} nodeTools - New node tools array or object with data property
   */
  updateNodeTools(nodeTools) {
    this.nodeTools = nodeTools;
    this._definitionsCache.clear();
    this._buildCache();
  }
}

// Singleton instance
let nodeDefinitionServiceInstance = null;

/**
 * Get or create the node definition service instance
 * @param {Array} nodeTools - Node tools array (required for first call)
 * @returns {NodeDefinitionService} The service instance
 */
export function getNodeDefinitionService(nodeTools) {
  if (!nodeDefinitionServiceInstance && nodeTools) {
    nodeDefinitionServiceInstance = new NodeDefinitionService(nodeTools);
  } else if (nodeDefinitionServiceInstance && nodeTools) {
    nodeDefinitionServiceInstance.updateNodeTools(nodeTools);
  }
  
  return nodeDefinitionServiceInstance;
}

/**
 * Main WorkflowNodeService - Facade for all node services
 */
export class WorkflowNodeService {
  constructor(nodeTemplates = {}) {
    this.nodeTemplates = nodeTemplates;
    this.behaviorService = new NodeBehaviorService(nodeTemplates);
    this.integrationService = new IntegrationMappingService(nodeTemplates);
    this.validationService = new FieldValidationService(nodeTemplates);
  }

  /**
   * Update node templates
   */
  updateNodeTemplates(nodeTemplates) {
    this.nodeTemplates = nodeTemplates;
    this.behaviorService = new NodeBehaviorService(nodeTemplates);
    this.integrationService = new IntegrationMappingService(nodeTemplates);
    this.validationService = new FieldValidationService(nodeTemplates);
  }

  /**
   * Process node for saving - apply behaviors and validations
   */
  async processNodeForSave(node) {
    const nodeType = node.type;
    const config = node.data?.config || {};

    // Validate configuration
    const errors = this.validationService.validateNodeConfig(nodeType, config);
    if (Object.keys(errors).length > 0) {
      throw new Error(`Validation errors: ${JSON.stringify(errors)}`);
    }

    // Apply field mappings for backend
    const mappedConfig = this.behaviorService.applyFieldMappings(nodeType, config, 'toBackend');

    // Ensure all original config fields are preserved (especially arrays like searchCriteria)
    const finalConfig = {
      ...config,
      ...mappedConfig
    };

    // Check integration requirements
    if (this.behaviorService.requiresIntegration(nodeType)) {
      const requirements = this.behaviorService.getIntegrationRequirements(nodeType, config);
      if (requirements) {
        const status = await this.integrationService.checkIntegrationStatus(
          requirements.service,
          requirements.statusAction
        );
        if (!status.connected) {
          toast.warning(`Integration not connected: ${requirements.service}`);
        }
      }
    }

    return {
      ...node,
      data: {
        ...node.data,
        config: finalConfig
      }
    };
  }

  /**
   * Process node for loading - apply behaviors and mappings
   */
  processNodeForLoad(node, existingConfig = {}) {
    const nodeType = node.type;
    const config = node.data?.config || {};

    // Get fields to preserve
    const preserveFields = this.behaviorService.getPreserveFields(nodeType);
    const preservedConfig = {};
    preserveFields.forEach(field => {
      if (existingConfig[field] !== undefined) {
        preservedConfig[field] = existingConfig[field];
      }
    });

    // Apply field mappings for UI
    const mappedConfig = this.behaviorService.applyFieldMappings(nodeType, config, 'toUI');

    return {
      ...node,
      data: {
        ...node.data,
        config: {
          ...mappedConfig,
          ...preservedConfig
        }
      }
    };
  }

  /**
   * Get node execution message
   */
  getNodeExecutionMessage(nodeType, config, hasOutput) {
    if (!hasOutput) {
      return this.behaviorService.getSuccessMessage(nodeType, config);
    }
    return null;
  }

  /**
   * Check if node should listen for events
   */
  shouldListenForEvents(nodeType, eventType) {
    return this.integrationService.listensForEvent(nodeType, eventType);
  }

  /**
   * Get all node behaviors
   */
  getNodeBehaviors(nodeType) {
    return this.behaviorService.getBehavior(nodeType);
  }

  /**
   * Check if node has multiple inputs
   */
  hasMultipleInputs(nodeType) {
    return this.behaviorService.hasMultipleInputs(nodeType);
  }

  /**
   * Get required services for node
   */
  getRequiredServices(nodeType) {
    return this.integrationService.getRequiredServices(nodeType);
  }

  /**
   * Check if node requires integration
   */
  requiresIntegration(nodeType) {
    return this.behaviorService.requiresIntegration(nodeType);
  }

  /**
   * Get integration requirements for a node
   */
  getIntegrationRequirements(nodeType, config) {
    return this.behaviorService.getIntegrationRequirements(nodeType, config);
  }
}

// Export singleton instance
export const workflowNodeService = new WorkflowNodeService();

// Export default
export default WorkflowNodeService;